import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { FetchError, useFetch } from '@/modules/core/composables/useFetch'
import { rails_url } from '@/modules/shared/utils/rails'
import { useRoute } from 'vue-router'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type AccessToken = string

type MagicLinkToken = string

export type Role = 'site_admin' | 'group_admin' | 'investor' | 'company_user'

type Slug = string

type Status = 'active' | 'inactive'

// Result shape so the router guard can tell "the slug in the URL doesn't match
// any group" (→ /not-found) apart from "session is dead" (→ logout). Without
// that distinction the guard logs the user out for both, which is the typo'd-
// subdomain logout bug (#2702).
// Not a discriminated union: the project compiles with `strict: false`, so TS
// can't narrow on a boolean discriminant and `result.reason` would not type-check.
export type RefreshResult = { ok: boolean; reason?: 'slug_not_found' | 'session_invalid' }

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type CurrentUser = {
  id: string | number
  email: string
  groups: any[]
  intercom_hash: string
  investor_id: string | number
  company_id: string | number | null
  initials: string
  managed_investor_set_ids: string[]
  managed_individual_ids: string[]
  first_name: string
  middle_name?: string | null
  last_name: string
  name: string
  profile_picture?: string
  // Raw group-admin boolean for the requested group (mirrors the backend's
  // RegisterInvestorUserService check) — `role` alone can't express
  // site_admin + group_admin of the current group.
  is_group_admin?: boolean
  role: Role
  status: Status
  filing_automation_enabled?: boolean
  impersonating?: boolean
  original_user_id?: string | number | null
  two_factor_enabled_at?: string
  two_factor_requested_at: string
}

const initialState: CurrentUser = {
  id: null,
  email: null,
  groups: [],
  intercom_hash: null,
  investor_id: null,
  company_id: null,
  first_name: null,
  middle_name: null,
  last_name: null,
  name: null,
  is_group_admin: false,
  role: null,
  two_factor_requested_at: null,
}

// How long a successful /auth/current response is trusted before the
// navigation guard asks the server again.
const REFRESH_TTL_MS = 60_000

export const useAuthStore = defineStore('authStore', () => {
  const route = useRoute()
  const access_token = useStorage<AccessToken>('access_token', null)
  const current_user = useStorage<CurrentUser>('current_user', { ...initialState })
  const slug = useStorage<Slug>('slug', null)
  const last_refreshed_at = useStorage<number>('last_refreshed_at', 0)
  const last_refreshed_slug = useStorage<Slug | null>('last_refreshed_slug', null)

  const has_current_user = computed(() => !!current_user.value.email)
  const is_authenticated = computed(() => !!access_token.value)
  const isInvestor = computed(() => current_user.value.role === 'investor')
  const is_site_admin = computed(() => current_user.value.role === 'site_admin')
  const is_group_admin = computed(() => current_user.value.role === 'group_admin')
  const is_site_or_group_admin = computed(
    () => current_user.value.role === 'site_admin' || current_user.value.role === 'group_admin',
  )
  const is_company_user = computed(() => current_user.value.role === 'company_user')
  const filing_automation_enabled = computed(() => current_user.value.filing_automation_enabled === true)
  const logged_in_as = computed(() => is_authenticated.value === true && current_user.value.impersonating === true)

  const logout = async () => {
    if (access_token.value) {
      try {
        await useFetch('/v4/auth/logout').post()
      } catch {
        // Swallow — we're clearing state regardless
      }
    }

    reset()
  }

  const validateToken = async () => {
    try {
      await useFetch('/v4/auth/token/validate').get()
      return true
    } catch {
      return false
    }
  }

  const reset = () => {
    access_token.value = null
    current_user.value = { ...initialState }
    slug.value = null
    last_refreshed_at.value = 0
    last_refreshed_slug.value = null
  }

  const refresh = async (token?: AccessToken, route?: any): Promise<RefreshResult> => {
    const forced = token !== undefined
    const routeSlug = route?.params?.slug as string | undefined

    // The router guard calls refresh on every navigation. When the same slug
    // was refreshed moments ago and no token swap is in play, trust the
    // cached session instead of blocking navigation on a round-trip. Slug
    // changes, ?t= token swaps, and logout always refresh for real.
    //
    // Staleness trade-off: on the skip path the group-role gate below reads
    // `current_user` from localStorage, so a role revoked server-side keeps
    // gating routes for up to REFRESH_TTL_MS — routing only; every API call
    // still enforces real authorization server-side.
    if (
      !forced &&
      routeSlug &&
      routeSlug === last_refreshed_slug.value &&
      is_authenticated.value &&
      Date.now() - last_refreshed_at.value < REFRESH_TTL_MS
    ) {
      return { ok: true }
    }

    access_token.value = token || access_token.value
    try {
      const result = await useFetch(`/v4/${routeSlug}/auth/current`).get<{ data: CurrentUser }>()
      current_user.value = result.data
      last_refreshed_at.value = Date.now()
      last_refreshed_slug.value = routeSlug ?? null
      return { ok: true }
    } catch (e) {
      console.error(e)
      current_user.value = { ...initialState }
      last_refreshed_at.value = 0
      last_refreshed_slug.value = null
      // Branch on the status, not the message: useFetch prefers the API's
      // `message` field over its `error` code, so a missing group surfaces as
      // "Couldn't find Group with subdomain = …" and never as "not_found".
      // A 404 here can only be the group lookup; a dead session returns 401.
      const reason = e instanceof FetchError && e.status === 404 ? 'slug_not_found' : 'session_invalid'
      return { ok: false, reason }
    }
  }

  const requestVerificationCode = async (channel: 'sms' | 'email') => {
    return useFetch('/v4/auth/send-verification-code').post({ channel })
  }

  const viewAs = async (user_id) => {
    if (!user_id) return
    window.location.href = `${rails_url(route.params.slug)}/login-as/${user_id}`
  }

  const requestEmailVerification = async (payload: { email: string; registration_type: 'company' | 'investor' }) => {
    try {
      const result = await useFetch(`/v4/${route.params.slug}/auth/request-email-verification`).post(payload)
      return { data: result?.data, error: null }
    } catch (e) {
      console.error(e.message)
      return { data: null, error: e.message }
    }
  }

  return {
    access_token,
    current_user,
    logged_in_as,
    slug,
    last_refreshed_at,
    last_refreshed_slug,

    has_current_user,
    is_authenticated,
    isInvestor,
    is_company_user,
    filing_automation_enabled,
    is_site_admin,
    is_site_or_group_admin,
    is_group_admin,

    logout,
    refresh,
    requestVerificationCode,
    reset,
    validateToken,
    viewAs,

    requestEmailVerification,
  }
})
